import type { ScaleBand, ScaleLinear, ScaleSymLog, ScaleTime } from 'd3-scale';

import {
  DateTimeFormatter,
  FormatterWithPrecision,
} from '../../core/types/formatter.js';
import type {
  AnyNumberScale,
  AnyScale,
  CategoricalScales,
  DateScales,
} from '../../core/types/scales.js';
import {
  type CATEGORICAL,
  TIME,
  type XYAxisAnyNumericalType,
  type XYAxisInternalTypes,
} from './axis.js';
import type { XYChartExploreState } from './state.js';
import {
  DOT_VARIANT,
  DotSeriesWithDatapoints,
  LINE_VARIANT,
  LineSeriesWithDatapoints,
  AREA_VARIANT,
  AreaSeriesWithDatapoints,
  XYChartAxisInternal,
  type XYChartColorScale,
  type XYChartLinearAxisInternalProps,
  type XYChartLogAxisInternalProps,
} from './xy-chart-internals.js';
import type {
  XYChartCategoricalAxisProps,
  XYChartTimeAxisProps,
} from './xy-chart-props.js';
import type {
  XYChartSelectionLinearMetadata,
  DotOrLineSelection,
} from './xy-chart-selection.js';
import type {
  DotDatapoint,
  LineDatapoint,
  AreaDatapoint,
  XYChartData,
} from './xy-chart.js';

/**
 * Type guard to check if the value is a primitive number
 * @param  value - a value to be checked
 * @returns return true if it's a number or false if it's not
 **/
export const isNumber = (value: unknown): value is number => {
  return typeof value === 'number';
};

/**
 * Type guard to check if the value is a primitive string
 * @param value - a value to be checked
 * @returns return true if it's a string or false if it's not
 **/
export const isString = (value: unknown): value is string => {
  return typeof value === 'string';
};

/**
 * Type guard to check if the value is a instance of Date object
 * @param value - a value to be checked
 * @returns return true if it's a Date object or false if it's not
 **/
export const isDate = (value: unknown): value is Date => {
  return value instanceof Date;
};

export const isFunction = <T = XYChartData, U = string>(
  fn: unknown,
): fn is (data: T) => U => {
  return typeof fn === 'function';
};

export interface DotScaleTime {
  scale: ScaleTime<number, number>;
  v0: Date;
  v1: Date | undefined;
}

export interface DotScaleLog {
  scale: ScaleSymLog<number, number>;
  v0: number;
}

export interface DotScaleLinear {
  scale: ScaleLinear<number, number>;
  v0: number;
  v1: undefined;
}

export interface DotScaleBand {
  scale: ScaleBand<string>;
  v0: string;
  v1: undefined;
}

export interface LineScaleTime {
  scale: ScaleTime<number, number>;
  v0: Date;
  v1: Date | undefined;
}

export interface LineScaleLog {
  scale: ScaleSymLog<number, number>;
  v0: number;
}

export interface LineScaleLinear {
  scale: ScaleLinear<number, number>;
  v0: number;
  v1: undefined;
}

export interface LineScaleBand {
  scale: ScaleBand<string>;
  v0: string;
  v1: undefined;
}

export interface BarScaleTime {
  scale: ScaleTime<number, number>;
  v0: Date | undefined;
  v1: Date | undefined;
}

export interface BarScaleLog {
  scale: ScaleSymLog<number, number>;
  v0: number | undefined;
  v1: number | undefined;
}

export interface BarScaleLinear {
  scale: ScaleLinear<number, number>;
  v0: number | undefined;
  v1: number | undefined;
}

export interface BarScaleBand {
  scale: ScaleBand<string>;
  v0: string | undefined;
  v1: undefined;
}

export interface AreaScaleTime {
  scale: ScaleTime<number, number>;
  v0: Date | undefined;
  v1: Date | undefined;
}

export interface AreaScaleLog {
  scale: ScaleSymLog<number, number>;
  v0: number | undefined;
  v1: number | undefined;
}

export interface AreaScaleLinear {
  scale: ScaleLinear<number, number>;
  v0: number | undefined;
  v1: number | undefined;
}

export interface AreaScaleBand {
  scale: ScaleBand<string>;
  v0: string | undefined;
  v1: string | undefined;
}

export interface TooltipCategorical {
  formatter: undefined;
  v0: string;
  v1: undefined;
}

export interface TooltipNumerical {
  formatter: FormatterWithPrecision;
  v0: number;
  v1: number;
}

export interface TooltipTime {
  formatter: DateTimeFormatter;
  v0: Date;
  v1: Date;
}

export interface NumericalHoveringPoint {
  v0: number;
  scale: ScaleLinear<number, number>;
}

export interface TimeHoveringPoint {
  v0: Date;
  scale: ScaleTime<number, number>;
}

export interface CategoricalHoveringPoint {
  v0: string;
  scale: ScaleBand<string>;
}

// accessors
export interface RectScaleLinear {
  scale: ScaleLinear<number, number>;
  v0: number;
  v1: number;
}

// accessors
export interface RectScaleLog {
  scale: ScaleSymLog<number, number>;
  v0: number;
  v1: number;
}

export interface RectScaleTime {
  scale: ScaleTime<number, number>;
  v0: Date;
  v1: Date;
}

export interface RectScaleBand {
  scale: ScaleBand<string>;
  v0: string;
  v1: never; // TODO test if we need this v1 here
}

const REGEX_LINEAR = /linear/;
const REGEX_LOG = /log/;
const REGEX_TIME = /^time$/;
const REGEX_CATEGORICAL = /^categorical$/;
const REGEX_NUMERICAL = /numerical/;
const REGEX_ANY_NUMBER = /^numerical|^time$/;

export const isLinear = (axisType: string) => REGEX_LINEAR.test(axisType);

export const isLogarithmic = (axisType: string) => REGEX_LOG.test(axisType);

export const isTime = (axisType: string) => REGEX_TIME.test(axisType);

export const isCategorical = (axisType: string) =>
  REGEX_CATEGORICAL.test(axisType);

export const isNumerical = (axisType: string) => REGEX_NUMERICAL.test(axisType);

export const isAnyNumber = (axisType: string) =>
  REGEX_ANY_NUMBER.test(axisType);

export function isLinearAxis(
  axis: XYChartAxisInternal,
): axis is XYChartAxisInternal & XYChartLinearAxisInternalProps {
  return isLinear(axis.type);
}

export function isLogarithmicAxis(
  axis: XYChartAxisInternal,
): axis is XYChartAxisInternal & XYChartLogAxisInternalProps {
  return isLogarithmic(axis.type);
}

export function isNumericalAxis(
  axis: XYChartAxisInternal,
): axis is XYChartAxisInternal &
  (XYChartLinearAxisInternalProps | XYChartLogAxisInternalProps) {
  return isNumerical(axis.type);
}

export function isAnyNumericalAxis<
  T extends { type: XYChartAxisInternal['type'] },
>(axis: T): axis is T & { type: XYAxisAnyNumericalType } {
  return !isCategorical(axis.type);
}

export function isTimeAxis(
  axis: XYChartAxisInternal,
): axis is XYChartAxisInternal & XYChartTimeAxisProps {
  return isTime(axis.type);
}

export function isCategoricalAxis(axis: {
  type: XYChartAxisInternal['type'];
}): axis is XYChartAxisInternal & XYChartCategoricalAxisProps {
  return isCategorical(axis.type);
}

export function isCategoricalScale(args: {
  axisType: XYAxisInternalTypes | undefined;
  axisScale: AnyScale | undefined;
}): args is { axisType: CATEGORICAL; axisScale: CategoricalScales } {
  return !!args.axisType && isCategoricalAxis({ type: args.axisType });
}

export function isTimeScale(args: {
  axisType: XYAxisInternalTypes | undefined;
  axisScale: AnyScale | undefined;
}): args is { axisType: TIME; axisScale: DateScales } {
  return !!args.axisType && isTime(args.axisType);
}

export function isAnyNumericalScale(args: {
  axisType: XYAxisInternalTypes | undefined;
  axisScale: AnyScale | undefined;
}): args is { axisType: XYAxisInternalTypes; axisScale: AnyNumberScale } {
  return !!args.axisType && isAnyNumericalAxis({ type: args.axisType });
}

export function isDotContentPoint(
  content: XYChartExploreState['content'],
): content is {
  type: typeof DOT_VARIANT;
  series: DotSeriesWithDatapoints;
  datapoint: DotDatapoint;
} {
  return (
    !!content &&
    content.type === DOT_VARIANT &&
    !!content.series &&
    !!content.datapoint
  );
}

export function isLineContentPoint(
  content: XYChartExploreState['content'],
): content is {
  type: typeof LINE_VARIANT;
  series: LineSeriesWithDatapoints;
  datapoint: LineDatapoint;
} {
  return (
    !!content &&
    content.type === LINE_VARIANT &&
    !!content.series &&
    !!content.datapoint
  );
}

export function isAreaContentPoint(
  content: XYChartExploreState['content'],
): content is {
  type: typeof AREA_VARIANT;
  series: AreaSeriesWithDatapoints;
  datapoint: AreaDatapoint;
} {
  return (
    !!content &&
    content.type === AREA_VARIANT &&
    !!content.series &&
    !!content.datapoint
  );
}

export function isNumericColoringScale(colorScale: XYChartColorScale): boolean {
  return colorScale.domain().every((value) => typeof value === 'number');
}

export const isValidCategoryValue = (value: unknown): value is string =>
  Boolean(value && typeof value === 'string');

export function isDotOrLineSelection(
  point: XYChartSelectionLinearMetadata,
): point is DotOrLineSelection {
  return (
    point.series.type === DOT_VARIANT || point.series.type === LINE_VARIANT
  );
}
